import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
} from '@mui/material';
import {
  NotificationsOutlined as NotificationsIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { useSidebar } from '../contexts/SidebarContext';
import { removeNotification } from '../store/slices/notificationSlice';
import { logout } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

const Navbar: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { notifications } = useAppSelector((state) => state.notifications);
  const dispatch = useAppDispatch();
  const { toggleSidebar } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [avatarAnchorEl, setAvatarAnchorEl] = useState<null | HTMLElement>(null);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const notificationMenuOpen = Boolean(notificationAnchorEl);
  const avatarMenuOpen = Boolean(avatarAnchorEl);
  
  // Filter notifications for current user
  const userNotifications = notifications.filter(
    notification => !notification.userId || notification.userId === user?.id
  );
  
  const unreadCount = userNotifications.length;

  // Get first letter of username for avatar
  const getInitials = (name: string) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };
  
  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };
  
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarAnchorEl(event.currentTarget);
  };
  
  const handleAvatarClose = () => {
    setAvatarAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleAvatarClose();
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleAvatarClose();
  };
  
  const handleNotificationItemClick = (notificationId: string) => {
    dispatch(removeNotification(notificationId));
    handleNotificationClose();
  };
  
  const handleClearAllNotifications = () => {
    if (user?.id) {
      // Clear only user-specific notifications
      userNotifications.forEach(notification => {
        dispatch(removeNotification(notification.id));
      });
    }
    setClearAllDialogOpen(false);
    handleNotificationClose();
  };

  const handleOpenClearAllDialog = () => {
    setClearAllDialogOpen(true);
  };

  const handleCancelClearAll = () => {
    setClearAllDialogOpen(false);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        borderRadius: 0,
        boxShadow: 'none',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 3,
        minHeight: '64px'
      }}>
        {/* Left side - Hamburger Menu (Mobile) or Logo (Desktop) */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          flex: isMobile ? '0 0 auto' : '1 1 auto'
        }}>
          {isMobile ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Open menu"
              onClick={toggleSidebar}
              sx={{
                color: '#666',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
             <Box
               onClick={() => navigate('/getting-started')}
               sx={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: 1,
                 cursor: 'pointer',
                 '&:hover': {
                   opacity: 0.8,
                 },
               }}
             >
              {/* AGRANA Logo with background */}
              <Box
                sx={{
                  backgroundColor: '#a31830',
                  borderRadius: 0,
                  px: 1.5,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src="/assets/agrana-logo.svg"
                  alt="AGRANA Logo"
                  style={{
                    height: '24px',
                    width: 'auto',
                    filter: 'brightness(0) invert(1)', // Make white SVG visible
                  }}
                />
              </Box>

              {/* Brand Text */}
              <Typography
                variant="h6"
                sx={{
                  color: '#333',
                  fontWeight: 500,
                  fontSize: '1rem',
                  ml: 0.5,
                }}
              >
                AI Hub
              </Typography>
             </Box>
           )}
        </Box>

        {/* Center - Logo and Brand (Mobile only) */}
        {isMobile && (
          <Box
            onClick={() => navigate('/getting-started')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: '1 1 auto',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            {/* AGRANA Logo with background */}
            <Box
              sx={{
                backgroundColor: '#a31830',
                borderRadius: 0,
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src="/assets/agrana-logo.svg"
                alt="AGRANA Logo"
                style={{
                  height: '24px',
                  width: 'auto',
                  filter: 'brightness(0) invert(1)', // Make white SVG visible
                }}
              />
            </Box>

            {/* Brand Text */}
            <Typography
              variant="h6"
              sx={{
                color: '#333',
                fontWeight: 500,
                fontSize: '0.85rem',
                ml: 0.5,
              }}
            >
              AI Hub
            </Typography>
          </Box>
        )}

        {/* Right side - Notifications and Avatar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          flex: '0 0 auto'
        }}>
          {/* Notification Bell */}
          <IconButton
            size="medium"
            onClick={handleNotificationClick}
            aria-label="View notifications"
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Badge 
              badgeContent={unreadCount} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.75rem',
                  minWidth: '18px',
                  height: '18px',
                }
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Avatar */}
          <Avatar
            onClick={handleAvatarClick}
            aria-label="Open user menu"
            sx={{
              width: 36,
              height: 36,
              backgroundColor: '#a31830',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {user?.username ? getInitials(user.username) : 'U'}
          </Avatar>
        </Box>
      </Toolbar>
      
      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={notificationMenuOpen}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {userNotifications.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="No notifications" />
          </MenuItem>
        ) : (
          <>
            {userNotifications.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationItemClick(notification.id)}
                sx={{
                  whiteSpace: 'normal',
                  py: 1.5,
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
              >
                <ListItemText
                   primary={notification.message}
                   secondary={new Date(notification.createdAt).toLocaleString()}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: notification.type === 'error' ? 600 : 400,
                    color: notification.type === 'error' ? '#d32f2f' : '#333'
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    color: '#666'
                  }}
                />
              </MenuItem>
            ))}
            {userNotifications.length > 0 && (
              <>
                <Divider />
                <MenuItem
                  onClick={handleOpenClearAllDialog}
                  sx={{
                    justifyContent: 'center',
                    color: '#666',
                    fontSize: '0.875rem',
                    py: 1
                  }}
                >
                  Clear All
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* Avatar Menu */}
      <Menu
        anchorEl={avatarAnchorEl}
        open={avatarMenuOpen}
        onClose={handleAvatarClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: 2,
          }
        }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile Settings" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>

      {/* Clear All Notifications Confirmation Dialog */}
      <Dialog
        open={clearAllDialogOpen}
        onClose={handleCancelClearAll}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Clear All Notifications?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all {userNotifications.length} notification{userNotifications.length !== 1 ? 's' : ''}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClearAll} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleClearAllNotifications} 
            variant="contained"
            sx={{
              backgroundColor: '#a31830',
              '&:hover': {
                backgroundColor: '#8b1426',
              },
            }}
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Navbar;
